/**
 * Named views on an account-config document (I-3024). A view is one member of
 * a map by id — a merge patch replaces an array whole, so a list the user
 * edits one item at a time is a map. `null` removes one id and leaves the
 * others. The payload (filters and sort, a query and a scope, …) is the
 * caller's; this module only knows the map.
 */
import { isJsonObject, type JsonObject } from "./mergePatch";

export const SAVED_VIEWS_FIELD = "views";

/** The map a document stores under `field` (default `views`). Not a map → {}. */
export function savedViewsRecord(
  raw: unknown,
  field: string = SAVED_VIEWS_FIELD,
): JsonObject {
  if (!isJsonObject(raw)) return {};
  const views = raw[field];
  return isJsonObject(views) ? views : {};
}

/** The place a new view takes: one past the highest stored `order`. */
export function nextSavedViewOrder(
  raw: unknown,
  field: string = SAVED_VIEWS_FIELD,
): number {
  let last = -1;
  for (const view of Object.values(savedViewsRecord(raw, field))) {
    if (!isJsonObject(view)) continue;
    const order = view.order;
    if (typeof order === "number" && Number.isFinite(order)) last = Math.max(last, order);
  }
  return last + 1;
}

/**
 * One map member as a merge patch. `entry` null removes that id. Objects
 * merge, so a later rename can send `{ name }` alone.
 */
export function savedViewsPatch(
  id: string,
  entry: JsonObject | null,
  field: string = SAVED_VIEWS_FIELD,
): JsonObject {
  return { [field]: { [id]: entry } };
}

/**
 * A compare key. Object keys are sorted and `undefined` members dropped;
 * array order stays, so a query's terms keep their place. Callers that must
 * treat an array as a set sort it before calling.
 */
export function savedViewSignature(value: unknown): string {
  return JSON.stringify(normalizeSavedViewValue(value));
}

function normalizeSavedViewValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeSavedViewValue);
  if (!isJsonObject(value)) return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([, item]) => item !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, item]) => [key, normalizeSavedViewValue(item)]),
  );
}
